import { useContext, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

const INITIAL_FORM = {
  email: "",
  password: "",
  confirmPassword: "",
  okToEmail: true,
};

export default function Register() {
  const navigate = useNavigate();
  const { style } = useStyle();
  const { refreshUser, isLoading } = useContext(UserContext);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(INITIAL_FORM);

  const panelBackground = style.color1 || "rgb(39, 55, 77)";
  const borderColor = style.color2 || "rgb(82, 109, 130)";
  const mutedText = style.color3 || "rgb(157, 178, 191)";
  const primaryText = style.color4 || "rgb(221, 230, 237)";
  const accentGreen = style.accentGreen || "rgb(24, 146, 80)";
  const inputBackground = "#0b111a";

  const emailIsValid = useMemo(() => {
    if (!data.email) {
      return true;
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      data.email.trim()
    );
  }, [data.email]);

  const passwordLengthValid =
    data.password.length >= 6;

  const passwordsMatch =
    !data.confirmPassword ||
    data.password === data.confirmPassword;

  const canSubmit = Boolean(
    data.email.trim() &&
      emailIsValid &&
      passwordLengthValid &&
      data.confirmPassword &&
      passwordsMatch &&
      !submitting &&
      !isLoading
  );

  const setField = (field, value) => {
    setData((previousData) => ({
      ...previousData,
      [field]: value,
    }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const registerUser = async (event) => {
    event.preventDefault();

    const email = data.email.trim();
    const {
      password,
      confirmPassword,
      okToEmail,
    } = data;

    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!emailIsValid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Please enter a password.");
      return;
    }

    if (!passwordLengthValid) {
      toast.error(
        "Your password must be at least 6 characters."
      );
      return;
    }

    if (!confirmPassword) {
      toast.error("Please confirm your password.");
      return;
    }

    if (!passwordsMatch) {
      toast.error("The passwords do not match.");
      return;
    }

    try {
      setSubmitting(true);

      const registerResponse = await axios.post(
        "/register",
        {
          email,
          password,
          okToEmail,
        }
      );

      if (registerResponse.data?.error) {
        toast.error(registerResponse.data.error);
        return;
      }

      const loginResponse = await axios.post("/login", {
        email,
        password,
      });

      if (loginResponse.data?.error) {
        toast.error(loginResponse.data.error);
        return;
      }

      const token = loginResponse.data?.token;

      if (!token) {
        throw new Error(
          "The login response did not include a token."
        );
      }

      localStorage.setItem("token", token);

      const authenticatedUser = await refreshUser(token);

      if (!authenticatedUser) {
        throw new Error(
          "Your account information could not be loaded."
        );
      }

      toast.success("Your account has been created.");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Registration failed:", error);

      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Unable to create your account right now."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sharedInputSx = {
    width: "100%",

    "& .MuiOutlinedInput-root": {
      minHeight: 48,
      color: primaryText,
      backgroundColor: inputBackground,
      borderRadius: "10px",
      transition:
        "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",

      "& fieldset": {
        borderColor: "rgba(255,255,255,0.11)",
      },

      "&:hover": {
        backgroundColor: "#0d1520",

        "& fieldset": {
          borderColor: "rgba(255,255,255,0.20)",
        },
      },

      "&.Mui-focused": {
        backgroundColor: "#0d1520",
        boxShadow: "0 0 0 3px rgba(24,146,80,0.14)",

        "& fieldset": {
          borderColor: accentGreen,
          borderWidth: "1px",
        },
      },

      "&.Mui-error fieldset": {
        borderColor: "#ef6c72",
      },

      "&.Mui-error.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(239,108,114,0.12)",
      },
    },

    "& .MuiInputLabel-root": {
      color: mutedText,
      fontSize: "0.94rem",

      "&.Mui-focused": {
        color: primaryText,
      },

      "&.Mui-error": {
        color: "#ef8a8f",
      },
    },

    "& .MuiInputBase-input": {
      color: primaryText,
      fontSize: "0.95rem",
    },

    "& .MuiInputAdornment-root": {
      color: mutedText,
    },

    "& .MuiFormHelperText-root": {
      mx: 0.25,
      mt: 0.35,
      color: mutedText,
      fontSize: "0.76rem",
      lineHeight: 1.25,
    },

    "& input:-webkit-autofill": {
      WebkitBoxShadow:
        `0 0 0 100px ${inputBackground} inset !important`,
      WebkitTextFillColor: `${primaryText} !important`,
      caretColor: `${primaryText} !important`,
    },
  };

  const primaryButtonSx = {
    minHeight: 48,
    borderRadius: "9px",
    textTransform: "none",
    fontSize: "0.96rem",
    fontWeight: 850,
    color: "#ffffff",
    backgroundColor: accentGreen,
    boxShadow: "0 9px 22px rgba(0,0,0,0.24)",
    transition:
      "transform 160ms ease, filter 160ms ease, box-shadow 160ms ease",

    "&:hover": {
      backgroundColor: accentGreen,
      filter: "brightness(1.08)",
      transform: "translateY(-1px)",
      boxShadow: "0 12px 26px rgba(0,0,0,0.30)",
    },

    "&.Mui-disabled": {
      color: "rgba(255,255,255,0.55)",
      backgroundColor: "rgba(24,146,80,0.40)",
      boxShadow: "none",
    },
  };

  const passwordVisibilityButton = (
    visible,
    setVisible,
    visibleLabel,
    hiddenLabel
  ) => (
    <IconButton
      type="button"
      aria-label={visible ? visibleLabel : hiddenLabel}
      onClick={() =>
        setVisible((currentValue) => !currentValue)
      }
      onMouseDown={handleMouseDownPassword}
      edge="end"
      sx={{
        color: mutedText,

        "&:hover": {
          color: primaryText,
          backgroundColor: "rgba(255,255,255,0.06)",
        },
      }}
    >
      {visible ? (
        <VisibilityOffRoundedIcon fontSize="small" />
      ) : (
        <VisibilityRoundedIcon fontSize="small" />
      )}
    </IconButton>
  );

  return (
    <>
      <Seo
        title="Create Account | Parish Audio"
        description="Create your Parish Audio account."
        canonical="https://parishaudio.com/register"
        robots="noindex,follow"
      />

      <Box
        component="main"
        sx={{
          width: "100%",
          minHeight: "calc(100vh - 130px)",
          px: { xs: 1.25, sm: 2 },
          py: { xs: 1.5, sm: 2.5 },
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 468,
            overflow: "hidden",
            borderRadius: "16px",
            backgroundColor: panelBackground,
            backgroundImage: "none",
            border: `1px solid ${borderColor}`,
            boxShadow:
              "0 28px 70px rgba(0,0,0,0.48), inset 0 1px 0 rgba(255,255,255,0.035)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              px: { xs: 2.1, sm: 2.8 },
              pt: { xs: 1.8, sm: 2.25 },
              pb: { xs: 1.4, sm: 1.7 },
              backgroundColor: "#111a26",
              borderBottom:
                "1px solid rgba(255,255,255,0.075)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 180,
                height: 180,
                top: -110,
                left: -70,
                borderRadius: "50%",
                backgroundColor: "rgba(24,146,80,0.13)",
                filter: "blur(3px)",
                pointerEvents: "none",
              }}
            />

            <Stack
              spacing={0.9}
              alignItems="center"
              textAlign="center"
              sx={{
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "11px",
                  color: "#ffffff",
                  backgroundColor: accentGreen,
                  border: "1px solid rgba(255,255,255,0.13)",
                  boxShadow:
                    "0 10px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.12)",
                }}
              >
                <PersonAddAltRoundedIcon />
              </Box>

              <Box>
                <Typography
                  component="h1"
                  sx={{
                    color: primaryText,
                    fontSize: {
                      xs: "1.4rem",
                      sm: "1.6rem",
                    },
                    fontWeight: 900,
                    lineHeight: 1.12,
                    letterSpacing: "-0.025em",
                  }}
                >
                  Create your account
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            component="form"
            onSubmit={registerUser}
            noValidate
            sx={{
              px: { xs: 2.1, sm: 2.8 },
              pt: { xs: 1.4, sm: 1.7 },
              pb: { xs: 1.7, sm: 2.15 },
            }}
          >
            <Stack spacing={0.85}>
              <TextField
                label="Email Address"
                type="email"
                autoComplete="email"
                autoFocus
                fullWidth
                disabled={submitting}
                value={data.email}
                onChange={(event) =>
                  setField("email", event.target.value)
                }
                error={Boolean(data.email) && !emailIsValid}
                helperText={
                  Boolean(data.email) && !emailIsValid
                    ? "Enter a valid email address."
                    : undefined
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={sharedInputSx}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                fullWidth
                disabled={submitting}
                value={data.password}
                onChange={(event) =>
                  setField("password", event.target.value)
                }
                error={
                  Boolean(data.password) &&
                  !passwordLengthValid
                }
                helperText={
                  !data.password
                    ? ""
                    : passwordLengthValid
                      ? "Password length is valid."
                      : "Use at least 6 characters."
                }
                FormHelperTextProps={{
                  sx: {
                    color: data.password
                      ? passwordLengthValid
                        ? "#72d49b"
                        : "#ef8a8f"
                      : mutedText,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {passwordVisibilityButton(
                        showPassword,
                        setShowPassword,
                        "Hide password",
                        "Show password"
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={sharedInputSx}
              />

              <TextField
                label="Confirm Password"
                type={
                  showConfirmPassword ? "text" : "password"
                }
                autoComplete="new-password"
                fullWidth
                disabled={submitting}
                value={data.confirmPassword}
                onChange={(event) =>
                  setField(
                    "confirmPassword",
                    event.target.value
                  )
                }
                error={
                  Boolean(data.confirmPassword) &&
                  !passwordsMatch
                }
                helperText={
                  !data.confirmPassword
                    ? undefined
                    : passwordsMatch
                      ? "Passwords match."
                      : "Passwords do not match."
                }
                FormHelperTextProps={{
                  sx: {
                    color:
                      data.confirmPassword && passwordsMatch
                        ? "#72d49b"
                        : undefined,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CheckCircleRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {passwordVisibilityButton(
                        showConfirmPassword,
                        setShowConfirmPassword,
                        "Hide confirmed password",
                        "Show confirmed password"
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={sharedInputSx}
              />

              <Paper
                elevation={0}
                sx={{
                  px: 0.85,
                  py: 0.25,
                  borderRadius: "9px",
                  backgroundColor: "rgba(4,7,12,0.30)",
                  border:
                    "1px solid rgba(255,255,255,0.065)",
                }}
              >
                <FormControlLabel
                  disabled={submitting}
                  control={
                    <Checkbox
                      checked={data.okToEmail}
                      onChange={(event) =>
                        setField(
                          "okToEmail",
                          event.target.checked
                        )
                      }
                      size="small"
                      sx={{
                        color: mutedText,
                        "&.Mui-checked": {
                          color: accentGreen,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        color: primaryText,
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        lineHeight: 1.35,
                      }}
                    >
                      Email me updates and offers
                    </Typography>
                  }
                  sx={{
                    width: "100%",
                    m: 0,
                    alignItems: "center",
                  }}
                />
              </Paper>

              <Button
                type="submit"
                fullWidth
                disabled={!canSubmit}
                endIcon={
                  submitting ? null : (
                    <PersonAddAltRoundedIcon fontSize="small" />
                  )
                }
                sx={primaryButtonSx}
              >
                {submitting ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <CircularProgress
                      size={17}
                      thickness={5}
                      sx={{ color: "#ffffff" }}
                    />
                    <span>Creating account...</span>
                  </Stack>
                ) : (
                  "Create Account"
                )}
              </Button>

              <Divider
                sx={{
                  my: 0.35,
                  borderColor: "rgba(255,255,255,0.075)",
                }}
              />

              <Stack spacing={0.6} alignItems="center">
                <Typography
                  sx={{
                    color: mutedText,
                    fontSize: "0.82rem",
                  }}
                >
                  Already have an account?
                </Typography>

                <Button
                  component={RouterLink}
                  to="/login"
                  fullWidth
                  startIcon={<LoginRoundedIcon fontSize="small" />}
                  sx={{
                    minHeight: 42,
                    borderRadius: "9px",
                    textTransform: "none",
                    fontWeight: 800,
                    color: primaryText,
                    backgroundColor:
                      "rgba(4,7,12,0.30)",
                    border:
                      "1px solid rgba(255,255,255,0.085)",

                    "&:hover": {
                      color: "#ffffff",
                      backgroundColor:
                        "rgba(4,7,12,0.48)",
                      borderColor:
                        "rgba(255,255,255,0.15)",
                    },
                  }}
                >
                  Sign In
                </Button>
              </Stack>

              <Stack
                spacing={0.2}
                alignItems="center"
                textAlign="center"
                sx={{ pt: 0.15 }}
              >
                <Typography
                  sx={{
                    color: mutedText,
                    fontSize: "0.72rem",
                    lineHeight: 1.4,
                  }}
                >
                  By creating an account, you agree to the
                  Parish Audio terms and conditions.
                </Typography>

                <Button
                  component={RouterLink}
                  to="/terms"
                  sx={{
                    minWidth: 0,
                    p: 0,
                    textTransform: "none",
                    color: primaryText,
                    fontSize: "0.74rem",
                    fontWeight: 800,

                    "&:hover": {
                      color: "#ffffff",
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Terms and Conditions
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </>
  );
}


// import { useState, useContext, useMemo } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import { toast } from "react-hot-toast";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import TermsModal from "../Terms/TermsModal";
// import Terms from "../Terms/Terms";
// import Seo from "../../components/Seo";

// import Button from "@mui/material/Button";
// import Box from "@mui/material/Box";
// import Paper from "@mui/material/Paper";
// import Stack from "@mui/material/Stack";
// import Typography from "@mui/material/Typography";
// import Divider from "@mui/material/Divider";
// import Checkbox from "@mui/material/Checkbox";
// import TextField from "@mui/material/TextField";
// import FormControl from "@mui/material/FormControl";
// import FormHelperText from "@mui/material/FormHelperText";
// import InputLabel from "@mui/material/InputLabel";
// import OutlinedInput from "@mui/material/OutlinedInput";
// import InputAdornment from "@mui/material/InputAdornment";
// import IconButton from "@mui/material/IconButton";
// import FormControlLabel from "@mui/material/FormControlLabel";
// import CircularProgress from "@mui/material/CircularProgress";

// import Visibility from "@mui/icons-material/Visibility";
// import VisibilityOff from "@mui/icons-material/VisibilityOff";
// import LoginIcon from "@mui/icons-material/Login";

// const initialForm = {
//   email: "",
//   password: "",
//   password2: "",
//   okToEmail: true,
// };

// export default function Register() {
//   const navigate = useNavigate();
//   const { style } = useStyle();
//   const { setUser, setUpdateFlag, isLoading } = useContext(UserContext);

//   const [isModalOpen, setModalOpen] = useState(false);
//   const [showPassword1, setShowPassword1] = useState(false);
//   const [showPassword2, setShowPassword2] = useState(false);
//   const [submitting, setSubmitting] = useState(false);
//   const [data, setData] = useState(initialForm);

//   const openModal = () => setModalOpen(true);
//   const closeModal = () => setModalOpen(false);

//   const handleMouseDownPassword = (event) => event.preventDefault();

//   const emailIsValid = useMemo(() => {
//     if (!data.email) return true;
//     return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim());
//   }, [data.email]);

//   const passwordLengthValid = data.password.length >= 6;
//   const passwordsMatch =
//     data.password2.length === 0 ? true : data.password === data.password2;

//   const canSubmit =
//     data.email.trim() &&
//     emailIsValid &&
//     passwordLengthValid &&
//     data.password2 &&
//     passwordsMatch &&
//     !submitting &&
//     !isLoading;

//   const navToLogin = () => navigate("/login");
//   const navToTerms = () => navigate("/terms");

//   const setField = (field, value) => {
//     setData((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const primaryButtonBg = "rgba(120, 150, 190, 0.14)";
//   const primaryButtonBorder = "rgba(140, 170, 210, 0.28)";
//   const primaryButtonHover = "rgba(120, 150, 190, 0.22)";

//   const sharedInputSx = {
//     "& .MuiInputBase-root": {
//       color: style.color4,
//       backgroundColor: style.darkerPanel || "rgba(6, 9, 14, 0.55)",
//       boxShadow: "none",
//       borderRadius: "10px",
//     },
//     "& .MuiOutlinedInput-root": {
//       "& fieldset": {
//         borderColor: "rgba(255,255,255,0.10)",
//         borderWidth: "1px",
//       },
//       "&:hover fieldset": {
//         borderColor: "rgba(255,255,255,0.16)",
//       },
//       "&.Mui-focused fieldset": {
//         borderColor: style.color3,
//         borderWidth: "1px",
//       },
//     },
//     "& .MuiInputLabel-root": {
//       color: style.color3,
//     },
//     "& .MuiInputLabel-root.Mui-focused": {
//       color: style.color4,
//     },
//     "& .MuiInputBase-input": {
//       color: style.color4,
//       fontSize: "0.95rem",
//     },
//     "& .MuiFormHelperText-root": {
//       marginLeft: 0,
//       marginTop: "4px",
//       minHeight: "20px",
//     },
//     "& input:-webkit-autofill": {
//       WebkitBoxShadow: `0 0 0 100px ${
//         style.darkerPanel || "rgba(6, 9, 14, 0.55)"
//       } inset !important`,
//       WebkitTextFillColor: `${style.color4} !important`,
//       caretColor: `${style.color4} !important`,
//     },
//   };

//   const registerUser = async (e) => {
//     e.preventDefault();

//     const email = data.email.trim();
//     const { password, password2, okToEmail } = data;

//     if (!email) {
//       toast.error("Please enter your email address.");
//       return;
//     }

//     if (!emailIsValid) {
//       toast.error("Please enter a valid email address.");
//       return;
//     }

//     if (!password) {
//       toast.error("Password cannot be blank.");
//       return;
//     }

//     if (password.length < 6) {
//       toast.error("Password must be at least 6 characters.");
//       return;
//     }

//     if (!password2) {
//       toast.error("Please confirm your password.");
//       return;
//     }

//     if (password !== password2) {
//       toast.error("Passwords do not match.");
//       return;
//     }

//     try {
//       setSubmitting(true);

//       const registerResponse = await axios.post("/register", {
//         email,
//         password,
//         okToEmail,
//       });

//       if (registerResponse.data?.error) {
//         toast.error(registerResponse.data.error);
//         return;
//       }

//       const loginResponse = await axios.post("/login", { email, password });

//       if (loginResponse.data?.error) {
//         toast.error(loginResponse.data.error);
//         return;
//       }

//       const userData = loginResponse.data;

//       await setUser(userData);
//       localStorage.setItem("token", userData.token);
//       await setUpdateFlag();

//       toast.success("Registration successful!");
//       window.location.href = "/";
//     } catch (error) {
//       console.error(error);
//       toast.error(
//         error?.response?.data?.error || "Unable to create your account right now."
//       );
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <>
//       <Seo
//         title="Create Account | Parish Audio"
//         description="Create your Parish Audio account."
//         canonical="https://parishaudio.com/register"
//         robots="noindex,follow"
//       />

//       <Box
//         sx={{
//           minHeight: "100%",
//           px: 2,
//           py: 2,
//           mt: 0,
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "flex-start",
//         }}
//       >
//         <Paper
//           elevation={0}
//           sx={{
//             width: "100%",
//             maxWidth: 500,
//             overflow: "hidden",
//             borderRadius: "10px",
//             backgroundColor: style.color1,
//             border: `1px solid ${style.color2}`,
//             boxShadow: "none",
//           }}
//         >
//           <Box
//             sx={{
//               px: { xs: 2.25, md: 3 },
//               pt: { xs: 2.5, md: 3 },
//               pb: 1.5,
//               borderBottom: `1px solid rgba(255,255,255,0.08)`,
//               backgroundColor: style.darkPanel || "rgba(9, 12, 18, 0.45)",
//             }}
//           >
//             <Stack spacing={1.25} alignItems="center" textAlign="center">
//               <Typography
//                 variant="h5"
//                 sx={{
//                   color: style.color4,
//                   fontWeight: 700,
//                   letterSpacing: "-0.02em",
//                 }}
//               >
//                 Create Account
//               </Typography>
//             </Stack>
//           </Box>

//           <Box
//             component="form"
//             onSubmit={registerUser}
//             sx={{
//               px: { xs: 2.25, md: 3 },
//               py: { xs: 2.25, md: 3 },
//             }}
//           >
//             <Stack spacing={1.25}>
//               <TextField
//                 label="Email Address"
//                 type="email"
//                 autoComplete="email"
//                 fullWidth
//                 size="small"
//                 value={data.email}
//                 onChange={(e) => setField("email", e.target.value)}
//                 error={!!data.email && !emailIsValid}
//                 helperText={
//                   !!data.email && !emailIsValid ? "Enter a valid email address." : " "
//                 }
//                 sx={sharedInputSx}
//               />

//               <FormControl fullWidth size="small" sx={sharedInputSx}>
//                 <InputLabel htmlFor="register-password">Password</InputLabel>
//                 <OutlinedInput
//                   id="register-password"
//                   type={showPassword1 ? "text" : "password"}
//                   value={data.password}
//                   autoComplete="new-password"
//                   onChange={(e) => setField("password", e.target.value)}
//                   endAdornment={
//                     <InputAdornment position="end">
//                       <IconButton
//                         aria-label="toggle password visibility"
//                         onClick={() => setShowPassword1((show) => !show)}
//                         onMouseDown={handleMouseDownPassword}
//                         edge="end"
//                         sx={{ color: style.color3 }}
//                       >
//                         {showPassword1 ? (
//                           <VisibilityOff fontSize="small" />
//                         ) : (
//                           <Visibility fontSize="small" />
//                         )}
//                       </IconButton>
//                     </InputAdornment>
//                   }
//                   label="Password"
//                 />
//                 <FormHelperText
//                   sx={{
//                     color: !data.password
//                       ? style.color3
//                       : passwordLengthValid
//                       ? "#66bb6a"
//                       : "#ef5350",
//                   }}
//                 >
//                   {!data.password
//                     ? " "
//                     : passwordLengthValid
//                     ? "Password looks good"
//                     : "Minimum 6 characters"}
//                 </FormHelperText>
//               </FormControl>

//               <FormControl fullWidth size="small" sx={sharedInputSx}>
//                 <InputLabel htmlFor="register-password-confirm">
//                   Confirm Password
//                 </InputLabel>
//                 <OutlinedInput
//                   id="register-password-confirm"
//                   type={showPassword2 ? "text" : "password"}
//                   value={data.password2}
//                   autoComplete="new-password"
//                   onChange={(e) => setField("password2", e.target.value)}
//                   endAdornment={
//                     <InputAdornment position="end">
//                       <IconButton
//                         aria-label="toggle confirm password visibility"
//                         onClick={() => setShowPassword2((show) => !show)}
//                         onMouseDown={handleMouseDownPassword}
//                         edge="end"
//                         sx={{ color: style.color3 }}
//                       >
//                         {showPassword2 ? (
//                           <VisibilityOff fontSize="small" />
//                         ) : (
//                           <Visibility fontSize="small" />
//                         )}
//                       </IconButton>
//                     </InputAdornment>
//                   }
//                   label="Confirm Password"
//                 />
//                 <FormHelperText
//                   sx={{
//                     color: !data.password2
//                       ? style.color3
//                       : passwordsMatch
//                       ? "#66bb6a"
//                       : "#ef5350",
//                   }}
//                 >
//                   {!data.password2
//                     ? " "
//                     : passwordsMatch
//                     ? "Passwords match"
//                     : "Passwords do not match"}
//                 </FormHelperText>
//               </FormControl>

//               <Box
//                 sx={{
//                   mt: 0.25,
//                   px: 1,
//                   py: 0.5,
//                   backgroundColor: style.darkPanel || "rgba(9, 12, 18, 0.45)",
//                   border: "1px solid rgba(255,255,255,0.06)",
//                   borderRadius: "10px",
//                 }}
//               >
//                 <FormControlLabel
//                   control={
//                     <Checkbox
//                       checked={data.okToEmail}
//                       onChange={(e) => setField("okToEmail", e.target.checked)}
//                       size="small"
//                       sx={{
//                         color: style.color3,
//                         "&.Mui-checked": {
//                           color: style.color4,
//                         },
//                       }}
//                     />
//                   }
//                   label={
//                     <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
//                       Email me updates and offers
//                     </Typography>
//                   }
//                   sx={{ my: 0 }}
//                 />
//               </Box>

//               <Button
//                 type="submit"
//                 disabled={!canSubmit}
//                 sx={{
//                   width: "100%",
//                   margin: "auto",
//                   textTransform: "none",
//                   fontWeight: 700,
//                   color: style.color4,
//                   backgroundColor: primaryButtonBg,
//                   borderWidth: "1px",
//                   borderColor: primaryButtonBorder,
//                   borderStyle: "solid",
//                   "&:hover": {
//                     backgroundColor: primaryButtonHover,
//                     borderColor: "rgba(160, 190, 230, 0.38)",
//                   },
//                   "&.Mui-disabled": {
//                     color: "rgba(255,255,255,0.45)",
//                     background: "rgba(255,255,255,0.08)",
//                     borderColor: "rgba(255,255,255,0.08)",
//                   },
//                 }}
//               >
//                 {submitting ? (
//                   <Stack direction="row" spacing={1} alignItems="center">
//                     <CircularProgress size={16} sx={{ color: style.color4 }} />
//                     <span>Creating account...</span>
//                   </Stack>
//                 ) : (
//                   "Create Account"
//                 )}
//               </Button>

//               <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 0.5 }} />

//               <Stack spacing={0.75} alignItems="center">
//                 <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                   Already have an account?
//                 </Typography>

//                 <Button
//                   onClick={navToLogin}
//                   startIcon={<LoginIcon fontSize="small" />}
//                   sx={{
//                     px: 2.25,
//                     py: 0.9,
//                     textTransform: "none",
//                     fontWeight: 700,
//                     fontSize: "0.92rem",
//                     color: style.color4,
//                     backgroundColor:
//                       style.darkPanel || "rgba(9, 12, 18, 0.45)",
//                     border: "1px solid rgba(255,255,255,0.08)",
//                     "&:hover": {
//                       backgroundColor:
//                         style.darkerPanel || "rgba(6, 9, 14, 0.55)",
//                     },
//                   }}
//                 >
//                   Go to Login
//                 </Button>
//               </Stack>

//               <Box sx={{ pt: 0.5, textAlign: "center" }}>
//                 <Typography
//                   sx={{
//                     color: style.color3,
//                     fontSize: "0.84rem",
//                     lineHeight: 1.5,
//                   }}
//                 >
//                   By creating an account, you agree to our terms and conditions.
//                 </Typography>

//                 <Button
//                   href="/terms"
//                   sx={{
//                     mt: 0.15,
//                     minWidth: 0,
//                     p: 0.25,
//                     textTransform: "none",
//                     fontWeight: 700,
//                     fontSize: "0.84rem",
//                     color: style.color4,
//                     "&:hover": {
//                       backgroundColor: "transparent",
//                       color: style.color3,
//                     },
//                   }}
//                 >
//                   View terms and conditions
//                 </Button>
//               </Box>
//             </Stack>
//           </Box>
//         </Paper>
//       </Box>
//     </>
//   );
// }

// import { useState, useContext, useMemo } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import { toast } from "react-hot-toast";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import TermsModal from "../Terms/TermsModal";
// import Terms from "../Terms/Terms";
// import Seo from "../../components/Seo";

// import Button from "@mui/material/Button";
// import Box from "@mui/material/Box";
// import Paper from "@mui/material/Paper";
// import Stack from "@mui/material/Stack";
// import Typography from "@mui/material/Typography";
// import Divider from "@mui/material/Divider";
// import Checkbox from "@mui/material/Checkbox";
// import TextField from "@mui/material/TextField";
// import FormControl from "@mui/material/FormControl";
// import FormHelperText from "@mui/material/FormHelperText";
// import InputLabel from "@mui/material/InputLabel";
// import OutlinedInput from "@mui/material/OutlinedInput";
// import InputAdornment from "@mui/material/InputAdornment";
// import IconButton from "@mui/material/IconButton";
// import FormControlLabel from "@mui/material/FormControlLabel";
// import CircularProgress from "@mui/material/CircularProgress";

// import Visibility from "@mui/icons-material/Visibility";
// import VisibilityOff from "@mui/icons-material/VisibilityOff";
// import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
// import LoginIcon from "@mui/icons-material/Login";

// const initialForm = {
//   email: "",
//   password: "",
//   password2: "",
//   okToEmail: true,
// };

// export default function Register() {
//   const navigate = useNavigate();
//   const { style } = useStyle();
//   const { setUser, setUpdateFlag, isLoading } = useContext(UserContext);

//   const [isModalOpen, setModalOpen] = useState(false);
//   const [showPassword1, setShowPassword1] = useState(false);
//   const [showPassword2, setShowPassword2] = useState(false);
//   const [submitting, setSubmitting] = useState(false);
//   const [data, setData] = useState(initialForm);

//   const openModal = () => setModalOpen(true);
//   const closeModal = () => setModalOpen(false);

//   const handleMouseDownPassword = (event) => event.preventDefault();

//   const emailIsValid = useMemo(() => {
//     if (!data.email) return true;
//     return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim());
//   }, [data.email]);

//   const passwordLengthValid = data.password.length >= 6;
//   const passwordsMatch =
//     data.password2.length === 0 ? true : data.password === data.password2;

//   const canSubmit =
//     data.email.trim() &&
//     emailIsValid &&
//     passwordLengthValid &&
//     data.password2 &&
//     passwordsMatch &&
//     !submitting &&
//     !isLoading;

//   const navToLogin = () => navigate("/login");
//   const navToTerms = () => navigate("/terms");

//   const setField = (field, value) => {
//     setData((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const sharedInputSx = {
//     "& .MuiInputBase-root": {
//       color: style.color4,
//       backgroundColor: style.color2,
//       boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
//     },
//     "& .MuiOutlinedInput-root": {
//       "& fieldset": {
//         borderColor: style.color3,
//         borderWidth: "1.25px",
//       },
//       "&:hover fieldset": {
//         borderColor: style.color4,
//       },
//       "&.Mui-focused fieldset": {
//         borderColor: style.color4,
//         borderWidth: "1.5px",
//       },
//     },
//     "& .MuiInputLabel-root": {
//       color: style.color3,
//     },
//     "& .MuiInputLabel-root.Mui-focused": {
//       color: style.color4,
//     },
//     "& .MuiInputBase-input": {
//       color: style.color4,
//       fontSize: "0.95rem",
//     },
//     "& .MuiFormHelperText-root": {
//       marginLeft: 0,
//       marginTop: "4px",
//       minHeight: "20px",
//     },
//     "& input:-webkit-autofill": {
//       WebkitBoxShadow: `0 0 0 100px ${style.color2} inset !important`,
//       WebkitTextFillColor: `${style.color4} !important`,
//       caretColor: `${style.color4} !important`,
//     },
//   };

//   const registerUser = async (e) => {
//     e.preventDefault();

//     const email = data.email.trim();
//     const { password, password2, okToEmail } = data;

//     if (!email) {
//       toast.error("Please enter your email address.");
//       return;
//     }

//     if (!emailIsValid) {
//       toast.error("Please enter a valid email address.");
//       return;
//     }

//     if (!password) {
//       toast.error("Password cannot be blank.");
//       return;
//     }

//     if (password.length < 6) {
//       toast.error("Password must be at least 6 characters.");
//       return;
//     }

//     if (!password2) {
//       toast.error("Please confirm your password.");
//       return;
//     }

//     if (password !== password2) {
//       toast.error("Passwords do not match.");
//       return;
//     }

//     try {
//       setSubmitting(true);

//       const registerResponse = await axios.post("/register", {
//         email,
//         password,
//         okToEmail,
//       });

//       if (registerResponse.data?.error) {
//         toast.error(registerResponse.data.error);
//         return;
//       }

//       const loginResponse = await axios.post("/login", { email, password });

//       if (loginResponse.data?.error) {
//         toast.error(loginResponse.data.error);
//         return;
//       }

//       const userData = loginResponse.data;

//       await setUser(userData);
//       localStorage.setItem("token", userData.token);
//       await setUpdateFlag();

//       toast.success("Registration successful!");
//       window.location.href = "/";
//     } catch (error) {
//       console.error(error);
//       toast.error(
//         error?.response?.data?.error || "Unable to create your account right now."
//       );
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <>
//       <Seo
//         title="Create Account | Parish Audio"
//         description="Create your Parish Audio account."
//         canonical="https://parishaudio.com/register"
//         robots="noindex,follow"
//       />

//       <Box
//         sx={{
//           minHeight: "100%",
//           px: 2,
//           py:2,
//           mt:0,
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "flex-start",
//         }}
//       >
//         <Paper
//           elevation={0}
//           sx={{
//             width: "100%",
//             maxWidth: 500,
//             overflow: "hidden",
//             borderRadius:'10px',
//             background: `linear-gradient(180deg, ${style.color1} 0%, rgba(39,55,77,0.96) 100%)`,
//             border: `1px solid ${style.color2}`,
//             boxShadow: "0 20px 50px rgba(0,0,0,0.32)",
//           }}
//         >
//           <Box
//             sx={{
//               px: { xs: 2.25, md: 3 },
//               pt: { xs: 2.5, md: 3 },
//               pb: 1.5,
//               borderBottom: `1px solid rgba(255,255,255,0.08)`,
//               background:
//                 "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
//             }}
//           >
//             <Stack spacing={1.25} alignItems="center" textAlign="center">

//               <Typography
//                 variant="h5"
//                 sx={{
//                   color: style.color4,
//                   fontWeight: 700,
//                   letterSpacing: "-0.02em",
//                 }}
//               >
//                 Create Account
//               </Typography>
//             </Stack>
//           </Box>

//           <Box
//             component="form"
//             onSubmit={registerUser}
//             sx={{
//               px: { xs: 2.25, md: 3 },
//               py: { xs: 2.25, md: 3 },
//             }}
//           >
//             <Stack spacing={1.25}>
//               <TextField
//                 label="Email Address"
//                 type="email"
//                 autoComplete="email"
//                 fullWidth
//                 size="small"
//                 value={data.email}
//                 onChange={(e) => setField("email", e.target.value)}
//                 error={!!data.email && !emailIsValid}
//                 helperText={
//                   !!data.email && !emailIsValid ? "Enter a valid email address." : " "
//                 }
//                 sx={sharedInputSx}
//               />

//               <FormControl fullWidth size="small" sx={sharedInputSx}>
//                 <InputLabel htmlFor="register-password">Password</InputLabel>
//                 <OutlinedInput
//                   id="register-password"
//                   type={showPassword1 ? "text" : "password"}
//                   value={data.password}
//                   autoComplete="new-password"
//                   onChange={(e) => setField("password", e.target.value)}
//                   endAdornment={
//                     <InputAdornment position="end">
//                       <IconButton
//                         aria-label="toggle password visibility"
//                         onClick={() => setShowPassword1((show) => !show)}
//                         onMouseDown={handleMouseDownPassword}
//                         edge="end"
//                         sx={{ color: style.color3 }}
//                       >
//                         {showPassword1 ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
//                       </IconButton>
//                     </InputAdornment>
//                   }
//                   label="Password"
//                 />
//                 <FormHelperText
//                   sx={{
//                     color:
//                       !data.password
//                         ? style.color3
//                         : passwordLengthValid
//                         ? "#66bb6a"
//                         : "#ef5350",
//                   }}
//                 >
//                   {!data.password
//                     ? " "
//                     : passwordLengthValid
//                     ? "Password looks good"
//                     : "Minimum 6 characters"}
//                 </FormHelperText>
//               </FormControl>

//               <FormControl fullWidth size="small" sx={sharedInputSx}>
//                 <InputLabel htmlFor="register-password-confirm">
//                   Confirm Password
//                 </InputLabel>
//                 <OutlinedInput
//                   id="register-password-confirm"
//                   type={showPassword2 ? "text" : "password"}
//                   value={data.password2}
//                   autoComplete="new-password"
//                   onChange={(e) => setField("password2", e.target.value)}
//                   endAdornment={
//                     <InputAdornment position="end">
//                       <IconButton
//                         aria-label="toggle confirm password visibility"
//                         onClick={() => setShowPassword2((show) => !show)}
//                         onMouseDown={handleMouseDownPassword}
//                         edge="end"
//                         sx={{ color: style.color3 }}
//                       >
//                         {showPassword2 ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
//                       </IconButton>
//                     </InputAdornment>
//                   }
//                   label="Confirm Password"
//                 />
//                 <FormHelperText
//                   sx={{
//                     color:
//                       !data.password2
//                         ? style.color3
//                         : passwordsMatch
//                         ? "#66bb6a"
//                         : "#ef5350",
//                   }}
//                 >
//                   {!data.password2
//                     ? " "
//                     : passwordsMatch
//                     ? "Passwords match"
//                     : "Passwords do not match"}
//                 </FormHelperText>
//               </FormControl>

//               <Box
//                 sx={{
//                   mt: 0.25,
//                   px: 1,
//                   py: 0.5,
//                   backgroundColor: "rgba(255,255,255,0.03)",
//                   border: "1px solid rgba(255,255,255,0.06)",
//                 }}
//               >
//                 <FormControlLabel
//                   control={
//                     <Checkbox
//                       checked={data.okToEmail}
//                       onChange={(e) => setField("okToEmail", e.target.checked)}
//                       size="small"
//                       sx={{
//                         color: style.color3,
//                         "&.Mui-checked": {
//                           color: style.color4,
//                         },
//                       }}
//                     />
//                   }
//                   label={
//                     <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
//                       Email me updates and offers
//                     </Typography>
//                   }
//                   sx={{ my: 0 }}
//                 />
//               </Box>

//               <Button
//                 type="submit"
//                 disabled={!canSubmit}
//                 //sx={{ my: 2, color: style.color4, display: 'block', backgroundColor: style.color1, filter: 'brightness(1.1)', boxShadow:5,fontSize: '14pt', width: '75%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 }, }}
//                 sx={{
//                   color: style.color3,
//                   fontWeight: 700,
//                   margin:'auto',
//                   width:'100%',
//                   backgroundColor: style.color1, 
//                   color:style.color4,
//                   borderWidth:'1px',
//                   borderColor:style.color3,
//                   filter:'brightness(1.1)',
//                   borderStyle:'solid',
//                   "&:hover": {

//                     backgroundColor: style.color2,
//                   },
//                   "&.Mui-disabled": {
//                     color: "rgba(255,255,255,0.45)",
//                     background: "rgba(255,255,255,0.08)",
//                     borderColor: "rgba(255,255,255,0.08)",
//                   },
//                 }}
//               >
//                 {submitting ? (
//                   <Stack direction="row" spacing={1} alignItems="center">
//                     <CircularProgress size={16} sx={{ color: style.color4 }} />
//                     <span>Creating account...</span>
//                   </Stack>
//                 ) : (
//                   "Create Account"
//                 )}
//               </Button>

//               <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 0.5 }} />

//               <Stack spacing={0.75} alignItems="center">
//                 <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                   Already have an account?
//                 </Typography>

//                 <Button
//                   onClick={navToLogin}
//                   startIcon={<LoginIcon fontSize="small" />}
//                   sx={{
//                     px: 2.25,
//                     py: 0.9,
//                     textTransform: "none",
//                     fontWeight: 700,
//                     fontSize: "0.92rem",
//                     color: style.color4,
//                     backgroundColor: "rgba(255,255,255,0.05)",
//                     border: "1px solid rgba(255,255,255,0.08)",
//                     "&:hover": {
//                       backgroundColor: "rgba(255,255,255,0.10)",
//                     },
//                   }}
//                 >
//                   Go to Login
//                 </Button>
//               </Stack>

//               <Box sx={{ pt: 0.5, textAlign: "center" }}>
//                 <Typography
//                   sx={{
//                     color: style.color3,
//                     fontSize: "0.84rem",
//                     lineHeight: 1.5,
//                   }}
//                 >
//                   By creating an account, you agree to our terms and conditions.
//                 </Typography>

//                 <Button
//                   href={"/terms"}
//                   sx={{
//                     mt: 0.15,
//                     minWidth: 0,
//                     p: 0.25,
//                     textTransform: "none",
//                     fontWeight: 700,
//                     fontSize: "0.84rem",
//                     color: style.color4,
//                     "&:hover": {
//                       backgroundColor: "transparent",
//                       color: style.color3,
//                     },
//                   }}
//                 >
//                   View terms and conditions
//                 </Button>
//               </Box>
//             </Stack>
//           </Box>
//         </Paper>
//       </Box>

//       {/* <TermsModal isOpen={isModalOpen} close={closeModal}>
//         <Terms />
//       </TermsModal> */}
//     </>
//   );
// }

// import { useState, useEffect, useContext } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import axios from 'axios';
// import Header from '../../components/Header/Header'
// import { toast } from 'react-hot-toast';
// import ReactGA from 'react-ga4';
// import{UserContext} from '../../context/userContext/userContext';
// import { useStyle } from "../../context/styleContext/styleContext"
// import TermsModal from "../Terms/TermsModal";
// import Terms from "../Terms/Terms";
// import Button from '@mui/material/Button';
// import Visibility from '@mui/icons-material/Visibility';
// import VisibilityOff from '@mui/icons-material/VisibilityOff';
// import { Checkbox, TextField, FormControl, FormHelperText, InputLabel, OutlinedInput, InputAdornment, IconButton, FormGroup, FormControlLabel } from "@mui/material";
// import Seo from "../../components/Seo";


// export default function Register() {
//   const navigate = useNavigate();
//   const [isModalOpen, setModalOpen] = useState(false);
//   const[okToSubmit, setHideButtons] = useState(false)
//   const openModal = () => setModalOpen(true);
//   const closeModal = () => setModalOpen(false);
//   const { style, setStyle } = useStyle();
//   const [showPassword1, setShowPassword1] = useState(false);
//   const [showPassword2, setShowPassword2] = useState(false);
//   const [passwordHelper, setPasswordHelper] = useState({ message: '', color: 'red' });
//   const [confirmPasswordHelper, setConfirmPasswordHelper] = useState({ message: '', color: '' });
//   const { setUser } = useContext(UserContext);
//   const { isLoading,  } = useContext(UserContext);
//   const { setUpdateFlag } = useContext(UserContext);

//   const handleClickShowPassword1 = () => setShowPassword1((show) => !show);
//   const handleClickShowPassword2 = () => setShowPassword2((show) => !show);
//   const handleMouseDownPassword = (event) => event.preventDefault();

//   const [data, setData] = useState({
//     email: '',
//     password: '',
//     password2: '',
//     okToEmail: true,
//   });

//   const navToLogin = () => {
//     navigate('/login');
//   };

//   const handlePasswordChange = (e) => {
//     const password = e.target.value;
//     setData({ ...data, password });

//     if (password.length >= 6 && password.length > 0) {
//       setPasswordHelper({ message: 'Password OK', color: 'green' });
//     } else {
//       setPasswordHelper({ message: 'Password must be 6 characters or more', color: 'red' });
//     }
//   };

//   const checkIfOK = (e) => {

//   }

//   const handleConfirmPasswordChange = (e) => {
//     const password2 = e.target.value;
//     setData({ ...data, password2 });

//     if(data.password.length < 1)
//     {
//         setConfirmPasswordHelper({message: '', color:'red'})
//     }
//     else if(password2.length < 1)
//     {
//         setConfirmPasswordHelper({message: '', color:'red'})
//     }
//     else if (password2 !== data.password) {
//       setConfirmPasswordHelper({ message: 'Passwords do not match', color: 'red' });
//     } else {
//       setConfirmPasswordHelper({ message: 'Passwords match', color: 'green' });
//     }
//   };

//   const registerUser = async (e) => {
//     e.preventDefault();
//     var okToSend = true;

//     const { email, password, password2, okToEmail } = data;
   

//     if (!email) {
//       okToSend = false;
//       toast.error("Error: You must enter your E-Mail address to create an account");
//     }

//     if (!password) {
//       okToSend = false;
//       toast.error("Error: Password cannot be blank");
//     }

//     if (!password2) {
//       okToSend = false;
//       toast.error("Error: Please enter your password again");
//     }

//     if (password !== password2) {
//       okToSend = false;
//       toast.error("Passwords do not match");
//     }

//     if (okToSend) {
//       try {

//         console.log("sending... ", email, "  " , password)

//         const { data } = await axios.post('/register', { email, password, okToEmail });

//         if (data.error) {
//           toast.error(data.error);
//         } else {
//           setData({});

//           const{data} = await axios.post('/login', {
//             email, 
//             password,
//           });
//           const userData = data;
//           if(data.error){

//             toast.error(data.error)
//         }   else {            

//             await setUser(userData);
//             localStorage.setItem('token', data.token)
//             toast.success(data.success)
//             await setUpdateFlag()
//             //window.location.href = `/`
//     }



//           toast.success('Registration Successful. Welcome!');
//          window.location.href = `/`
//         }

//       } catch (error) {
//         console.log(error);
//       }
//     }
//   };

//   return (
//     <>
//     <Seo
//       title="Create Account | Parish Audio"
//       description="Create your Parish Audio account to manage purchases, downloads, subscriptions, and plugin access."
//       canonical="https://parishaudio.com/register"
//       robots="noindex,follow"
//     />

//       <div style={{ display: 'flex', flexDirection: 'column', width: '100%', marginTop: '25px'}}>
//         <div style={{ display: 'flex', flexDirection: 'column', width: '90%', maxWidth: '600px', margin: 'auto', color: style.color4, textAlign: 'center', marginTop: '5px', backgroundColor: style.color1, borderRadius: '10px', borderStyle: 'solid', borderColor: style.color1, borderWidth: '5px', marginBottom:'25px' }}>
//           <div style={{ width: '95%', margin: 'auto' }}>
//             <div style={{ display: 'flex', flex: 1, textAlign: 'center', marginTop: '10px' }}>
//               <h1 style={{ flex: 1, textAlign: 'center', margin: 'auto', marginBottom: '25px' }}>
//                 Create Your Account
//               </h1>
//             </div>
//             <div style={{ maxWidth: '400px', margin: 'auto' }}>
//               <div style={{ display: 'flex', flex: 1, textAlign: 'center', minWidth: '300px', margin: 'auto', marginBottom: '10px' }}>
//                 <h4 style={{ flex: 1, textAlign: 'left', margin: 'auto', color: style.color1 }}>
//                   <div style={{  }}>
//                     <TextField
//                       id="outlined-basic"
//                       label="Email"
//                       variant="outlined"
//                       value={data.email}
//                       onChange={(e) => setData({ ...data, email: e.target.value })}
//                       size='small'
//                       fullWidth
//                       sx={{
//                         backgroundColor: style.color2,
//                         borderRadius:'4px',
//                         '& .MuiInputBase-input': {
//                         color: style.color4,  // Change the font color here
//                         fontSize: '14pt',  // Change the font size here
//                         boxShadow:5,
                                                        
//                         },
//                         '& .MuiOutlinedInput-root': {
//                         '& fieldset': {
//                             borderColor: style.color1, // Change the border color if needed
//                             borderWidth:'2px'
//                         },
//                         '&:hover fieldset': {
//                             borderColor: style.color1, // Change the border color on hover if needed\
//                             borderWidth:'3px',                                                               
                     
                            
//                         },
//                         '&.Mui-focused fieldset': {
//                             borderColor: style.color1, // Change the border color when focused if needed
//                             borderWidth:'3px'
                            
//                         },
//                         '&.Mui-disabled': {
//                             '& fieldset': {
//                             borderColor: style.color1, // Change the border color when disabled
//                             },
//                         },
//                         },
//                         '& .MuiInputLabel-root': {
//                         color: style.color3, // Change the label color here
//                         fontSize:'14pt',
//                         },
//                         '& .MuiInputLabel-root.Mui-focused': {
//                         color: style.color3, // Change the label color when focused here                                 
//                         },
//                         '& .MuiInputLabel-root.Mui-disabled': {
//                         color: style.color2, // Change the label color when disabled
//                         },
//                         '& .MuiInputBase-root.Mui-disabled': {
//                         '& input': {
//                             color: style.color3, // Change the input font color when disabled // so it works in safari
//                             WebkitTextFillColor: style.color4, // so it works on chrome
//                             fontSize:'14pt'
//                         },
//                         },
//                         '& .MuiOutlinedInput-input': { color: style.color4,
//                         '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset',
//                             WebkitTextFillColor: style.color4, color:style.color4
//                          }, }, 
//                     }}
//                     />
//                   </div>
//                 </h4>
//               </div>

//               <div style={{ display: 'flex', flex: 1, flexDirection:'column', marginBottom: '10px', width: '100%', backgroundColor:style.color1}}>
//               <FormControl sx={{
//                   backgroundColor: style.color2,
//                   flex: 1,
//                   width:'100%',
//                   size: 'small',
//                   margin: 'auto',
//                   boxShadow:5,

                  

//                   '& .MuiInputBase-input': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                     size: 'small',
//                   },
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: style.color1,
//                       borderWidth: '2px'
//                     },
//                     '&:hover fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-focused fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-disabled': {
//                       '& fieldset': {
//                         borderColor: style.color1,
//                       },
//                     },
//                   },
//                   '& .MuiInputLabel-root': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                   },
//                   '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                       color: style.color3,
//                       WebkitTextFillColor: style.color1,
//                       fontSize: '14pt'
//                     },
//                   },
//                   '& .MuiOutlinedInput-input': {
//                     color: style.color4,
//                     '& input:-webkit-autofill': {
//                     WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                     WebkitTextFillColor: `${style.color4} !important`,
//                     color: `${style.color4} !important`,
//                     caretColor: `${style.color4} !important`,
//                 },
//                   },
//                 }} variant="outlined">
//                   <InputLabel size="small" htmlFor="outlined-adornment-password">Password</InputLabel>
//                   <OutlinedInput
//                     id="outlined-adornment-password"
//                     type={showPassword1 ? 'text' : 'password'}
//                     value={data.password}
//                     size='small'
//                     onChange={handlePasswordChange}
//                     sx={{
//                         '& input:-webkit-autofill': {
//                         WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                         WebkitTextFillColor: `${style.color4} !important`,
//                         color: `${style.color4} !important`,
//                         caretColor: `${style.color4} !important`,
//                     },
//                     }}
//                     endAdornment={
//                       <InputAdornment position="end">
//                         <IconButton
//                           aria-label="toggle password visibility"
//                           onClick={handleClickShowPassword1}
//                           onMouseDown={handleMouseDownPassword}
//                           edge="end"
//                           sx={{ color: style.color1 }}
//                         >
//                           {showPassword1 ? <VisibilityOff /> : <Visibility />}
//                         </IconButton>
//                       </InputAdornment>
//                     }
//                     label="Password"
//                   />
  
//                  </FormControl>
          
//                 {/* <FormControl sx={{
//                   backgroundColor: style.color2,
//                   flex: 1,
//                   width:'100%',
//                   size: 'small',
//                   margin: 'auto',
//                   '& .MuiInputBase-input': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                     size: 'small',
//                   },
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: style.color1,
//                       borderWidth: '2px'
//                     },
//                     '&:hover fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-focused fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-disabled': {
//                       '& fieldset': {
//                         borderColor: style.color1,
//                       },
//                     },
//                   },
//                   '& .MuiInputLabel-root': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                   },
//                   '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                       color: style.color3,
//                       WebkitTextFillColor: style.color1,
//                       fontSize: '14pt'
//                     },
//                   },
//                   '& .MuiOutlinedInput-input': {
//                     color: style.color4,
//                     '&:-webkit-autofill': {
//                       WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                       WebkitTextFillColor: `${style.color4} !important`,
//                       color: `${style.color4} !important`,
//                       caretColor: `${style.color4} !important`,
//                     },
//                   },
//                 }} variant="outlined">
//                   <InputLabel size="small" htmlFor="outlined-adornment-password">Password</InputLabel>
//                   <OutlinedInput
//                     id="outlined-adornment-password"
//                     type={showPassword1 ? 'text' : 'password'}
//                     value={data.password}
//                     size='small'
//                     sx={{
//                       '& input:-webkit-autofill': {
//                       WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                       WebkitTextFillColor: `${style.color4} !important`,
//                       color: `${style.color4} !important`,
//                       caretColor: `${style.color4} !important`,
//                   },
//                   }}
//                     onChange={handlePasswordChange}
//                     endAdornment={
//                       <InputAdornment position="end">
//                         <IconButton
//                           aria-label="toggle password visibility"
//                           onClick={handleClickShowPassword1}
//                           onMouseDown={handleMouseDownPassword}
//                           edge="end"
//                           sx={{ color: style.color1 }}
//                         >
//                           {showPassword1 ? <VisibilityOff /> : <Visibility />}
//                         </IconButton>
//                       </InputAdornment>
//                     }
//                     label="Password"
//                   />
  
//                  </FormControl> */}

//                     <FormHelperText sx={{ color: passwordHelper.color, backgroundColor:style.color1, fontSize: '12pt', width:'100%' }}>
//                         {passwordHelper.message}
//                   </FormHelperText>
//               </div>

//               <div style={{ display: 'flex', flex: 1, flexDirection:'column', marginBottom: '20px', width: '100%', backgroundColor:style.color1  }}>
//               <FormControl sx={{
//                   backgroundColor: style.color2,
//                   flex: 1,
//                   width:'100%',
//                   size: 'small',
//                   margin: 'auto',
//                   boxShadow:5,

                  

//                   '& .MuiInputBase-input': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                     size: 'small',
//                   },
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: style.color1,
//                       borderWidth: '2px'
//                     },
//                     '&:hover fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-focused fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-disabled': {
//                       '& fieldset': {
//                         borderColor: style.color1,
//                       },
//                     },
//                   },
//                   '& .MuiInputLabel-root': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                   },
//                   '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                       color: style.color3,
//                       WebkitTextFillColor: style.color1,
//                       fontSize: '14pt'
//                     },
//                   },
//                   '& .MuiOutlinedInput-input': {
//                     color: style.color4,
//                     '& input:-webkit-autofill': {
//                     WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                     WebkitTextFillColor: `${style.color4} !important`,
//                     color: `${style.color4} !important`,
//                     caretColor: `${style.color4} !important`,
//                 },
//                   },
//                 }} variant="outlined">
//                   <InputLabel size="small" htmlFor="outlined-adornment-password">Password</InputLabel>
//                   <OutlinedInput
//                     id="outlined-adornment-password"
//                     type={showPassword2 ? 'text' : 'password'}
//                     value={data.password2}
//                     size='small'
//                     onChange={handleConfirmPasswordChange}
//                     sx={{
//                         '& input:-webkit-autofill': {
//                         WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                         WebkitTextFillColor: `${style.color4} !important`,
//                         color: `${style.color4} !important`,
//                         caretColor: `${style.color4} !important`,
//                     },
//                     }}
//                     endAdornment={
//                       <InputAdornment position="end">
//                         <IconButton
//                           aria-label="toggle password visibility"
//                           onClick={handleClickShowPassword2}
//                           onMouseDown={handleMouseDownPassword}
//                           edge="end"
//                           sx={{ color: style.color1 }}
//                         >
//                           {showPassword2 ? <VisibilityOff /> : <Visibility />}
//                         </IconButton>
//                       </InputAdornment>
//                     }
//                     label="Password Again"
//                   />
  
//                  </FormControl>
        
                
//                 <FormHelperText sx={{ color: confirmPasswordHelper.color, backgroundColor:style.color1, fontSize: '12pt' }}>
//                     {confirmPasswordHelper.message}
//                   </FormHelperText>

//                   {/* <FormGroup>
//                     <FormControlLabel checked={data.okToEmail} onChange={(e) => setData({ ...data, okToEmail: e.target.checked }) } control={<Checkbox size='large' sx={{color:style.color1, '&.Mui-checked': {
//                         color: style.color1,
//                     },}} />} label="Subscribe to Emails" />
//                 </FormGroup> */}
//               </div>


              
//                 <div style={{ marginTop: '25px', marginBottom: '30px', display: 'flex', flex: 1 }}>
//                   <Button onClick={registerUser} sx={{ my: 2, color: style.color4, display: 'block', backgroundColor: style.color1, filter: 'brightness(1.1)', boxShadow:5,fontSize: '14pt', width: '75%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 }, }}>Create Account</Button>
//                 </div>
              
//               {/* {(data.password.length < 6 || data.password != data.password2) && (
//                 <div style={{ marginTop: '50px', marginBottom: '50px', display: 'flex', flex: 1 }}>
//                   <Button disabled onClick={registerUser} sx={{ my: 2, color: style.color5, display: 'block', backgroundColor: style.color3, fontSize: '16pt', width: '100%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color4, borderWidth: '2px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color4 }, }}>Create Account</Button>
//                 </div>
//               )} */}

//               <div style={{ marginBottom: '20px' }}>
//                 <h4 style={{marginBottom:'10px'}}>Already have an account?</h4>
//                 <Button onClick={navToLogin} sx={{ my: 2, color: style.color3, display: 'block', margin: 'auto', boxShadow:5,backgroundColor: style.color1, filter: 'brightness(1.1)', fontSize: '12pt', width: '75%', minWidth: '150px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 } }}>
//                   <strong>Login</strong>
//                 </Button>
//               </div>
//               <div style={{ flex: 1, borderBottomLeftRadius: '15px', color: style.color5, marginBottom: '25px' }}>
//                 By using this website, you agree to our
//                 <div>
//                   <Button sx={{ my: 2, color: style.color3, display: 'block', margin: 'auto', backgroundColor: style.color1, fontSize: '12pt', width: '100%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color1, borderWidth: '2px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color1, color:style.color4 } }} onClick={setModalOpen.bind(null, true)}><strong>terms and conditions</strong></Button>
//                 </div>
//               </div>

//               <div id='modal-root'>
//                 <TermsModal isOpen={isModalOpen} close={closeModal}>
//                   <Terms />
//                 </TermsModal>
//               </div>
//             </div>
//           </div>
//         </div>
//         <div id='modal-root'></div>
//       </div>
//     </>
//   );
// }




